/* NewDealModal — create a Deal record.
 *
 * Per §7.8 — vstupní body „+ Nový obchod" vždy předvyplní klienta.
 *   Pokud je modal otevřen s `contactId` prop, klient je zamčen.
 *   Jinak nabídne select s existujícími klienty + rychlý "Nový lead".
 *
 * REALITNÍ OBCHOD (od 9/2026, jen se `povolitReality`)
 * ───────────────────────────────────────────────────
 * Nemovitost se zakládá JINAK než hypotéka a formulář se tomu přizpůsobí:
 *
 *   PRODEJ  → z velkého pole nahoře se stane ADRESA a modal volá
 *             `POST /api/zakazky`, které založí zakázku I obchod naráz.
 *             Kdyby modal poslal obyčejný `POST /api/deals`, vznikl by obchod
 *             bez nemovitosti — bez adresy, bez prohlídek, bez inzerátu — a
 *             makléř by musel zakládat podruhé jinde.
 *   KOUPĚ   → vybere se konkrétní nemovitost z mých zakázek. Bez ní by karta
 *             kupujícího neuměla odpovědět na jedinou otázku, kvůli které
 *             existuje („kterou nemovitost, jak je to s rezervací").
 *
 * NIC SE TIŠE NEZAHAZUJE. Pole, která u nemovitosti nedávají smysl (BJ,
 * poskytovatel, pravděpodobnost, šipky fází), se schovají — ne aby se vyplněná
 * hodnota po odeslání ztratila, ale aby se vůbec nedala vyplnit.
 *
 * ⚠️ VOLAJÍCÍ BEZ `povolitReality` SE NEMĚNÍ. /obchody i hypoteční pipeline
 * dostanou tentýž seznam produktů jako dřív.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PRODUCT_META, BOARD_COLUMNS } from "@/lib/dealMeta";
import ClientAutocomplete, { invalidateClientCache, contactFullName } from "@/components/crm/ClientAutocomplete";
import { createContact } from "@/lib/crmApi";
import { nabidniNemovitost, zalozPoptavku } from "@/lib/reality/poptavkyApi";
import { nactiZakazky, zalozZakazku } from "@/lib/zakazkyApi";
import { useLanguage } from "@/contexts/LanguageContext";
import { zavriNaPozadi } from "@/lib/modalDirtyGuard";
import Okno from "@/components/ui/Okno";

const PRODUCT_OPTIONS = [
  "hypoteka", "refinancovani", "podnikatelsky_uver", "spotrebitelsky_uver",
  "pojisteni_zivot", "pojisteni_auta", "pojisteni_majetek",
  "investice", "penzijko", "stavebni", "jine",
];

/** Realitní typy. Přidávají se na konec — pořadí finančních se nemění. */
const PRODEJ = "prodej_nemovitosti";
const KOUPE = "koupe_nemovitosti";
const REALITNI_OPTIONS = [PRODEJ, KOUPE];
const TYPY_NEMOVITOSTI = ["byt", "dum", "pozemek", "komercni"];

export default function NewDealModal({ open, onClose, onCreated, onCreatedStay, contactId, contactName, defaultBoardStage, povolitReality = false, predvolenyTyp = null }) {
  const { t, currency, lang } = useLanguage();
  // Map board → first stage_index in flow.
  const initialStageForBoard = (board) => {
    if (!board) return 0;
    return { analyza: 0, proces: 1, podpis: 4, cerpani: 6 }[board] ?? 0;
  };
  const [form, setForm] = useState({
    contact_id: contactId || "",
    title: "",
    product_type: predvolenyTyp || "hypoteka",
    provider: "",
    value_bj: "",
    value_kc: "",
    notes: "",
    stage_index: initialStageForBoard(defaultBoardStage),
    probability: 50,
    // Realitní pole. Prázdné = nezadáno, ne nula (u ceny bytu jde o miliony).
    typ_nemovitosti: "byt",
    zakazka_id: "",
  });
  const [boardStage, setBoardStage] = useState(defaultBoardStage || "analyza"); // šipkový volič fáze (Raynet)
  const [saving, setSaving] = useState(false);
  /* Moje zakázky pro výběr „kterou nemovitost kupuje".
     null = ještě nenačteno, [] = opravdu žádná, false = načtení selhalo. */
  const [zakazky, setZakazky] = useState(null);
  const [newClientObj, setNewClientObj] = useState(null); // klient založený rovnou z výběru (není v cache autocompletu)

  // „Čeho se to týká" — když klient neexistuje, založ ho rovnou z napsaného textu.
  // První slovo = jméno, zbytek = příjmení (Jakub Sekula → name=Jakub, surname=Sekula).
  const createNewClient = async (q) => {
    const parts = (q || "").trim().split(/\s+/).filter(Boolean);
    if (!parts.length) { toast.error(t("auto.newDealModal.napisNejdrivJmenoNoveho", 'Napiš nejdřív jméno nového klienta do vyhledávání')); return; }
    try {
      const c = await createContact({ name: parts[0], surname: parts.slice(1).join(" ") });
      invalidateClientCache();
      setNewClientObj(c);
      set("contact_id", c.id);
      toast.success(lang === 'sk' ? `Klient ${contactFullName(c)} založený` : `Klient ${contactFullName(c)} založen`);
    } catch {
      toast.error(t("auto.newDealModal.klientaSeNepodariloZalozit", 'Klienta se nepodařilo založit'));
    }
  };

  useEffect(() => {
    if (!open) return;
    setForm({
      contact_id: contactId || "",
      title: "",
      product_type: predvolenyTyp || "hypoteka",
      provider: "",
      value_bj: "",
      value_kc: "",
      notes: "",
      stage_index: initialStageForBoard(defaultBoardStage),
      probability: 50,
      typ_nemovitosti: "byt",
      zakazka_id: "",
    });
    setBoardStage(defaultBoardStage || "analyza");
    setNewClientObj(null);
    setZakazky(null);
  }, [open, contactId, defaultBoardStage, predvolenyTyp]);

  /* Seznam nemovitostí se tahá, teprve když si makléř vybere KOUPI. Poradce,
     který o realitách neví, tak nezaplatí ani jedno volání navíc. */
  useEffect(() => {
    if (!open || form.product_type !== KOUPE || zakazky !== null) return;
    let zivy = true;
    nactiZakazky()
      .then((d) => { if (zivy) setZakazky(d.zakazky || []); })
      /* Selhání NENÍ prázdný seznam. „Nemáš žádnou nemovitost" by makléře
         poslalo zakládat zakázku, kterou už dávno má. */
      .catch(() => { if (zivy) setZakazky(false); });
    return () => { zivy = false; };
  }, [open, form.product_type, zakazky]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  if (!open) return null;

  const jeProdej = povolitReality && form.product_type === PRODEJ;
  const jeKoupe = povolitReality && form.product_type === KOUPE;
  const jeReality = jeProdej || jeKoupe;

  /* Prodej: zakázka + obchod naráz přes `POST /api/zakazky`. Velké pole nahoře
     je v tomhle režimu ADRESA — název obchodu z ní složí server („Byt <adresa>"),
     takže se nic z napsaného neztratí. */
  const submitProdej = async () => {
    const zakazka = await zalozZakazku({
      contact_id: form.contact_id,
      adresa: form.title.trim(),
      typ: form.typ_nemovitosti,
      // Nezadaná cena zůstává nezadaná. Nula by tvrdila, že prodáváme zadarmo.
      cena_nabidkova: form.value_kc === "" ? undefined : Number(form.value_kc),
      poznamka: form.notes.trim() || undefined,
    });
    if (!zakazka || !zakazka.id) throw new Error("neocekavana-odpoved");
    return zakazka;
  };

  /* Koupě: POPTÁVKA přes `POST /api/reality/poptavky`. Nemovitost je
     VOLITELNÁ — kupující ji v okamžiku, kdy se ozve, ještě nemá vybranou
     a devět z deseti poptávek začíná právě takhle. Kdyby ji modal vyžadoval,
     makléř by si kupujícího nezaložil vůbec a zůstal by mu na papíře.
     Vybraná nemovitost se uloží jako první NABÍDKA, ne jako podmínka vzniku. */
  const submitKoupe = async () => {
    const poptavka = await zalozPoptavku({
      contact_id: form.contact_id,
      nazev: form.title.trim(),
      poznamka: form.notes.trim() || undefined,
    });
    if (!poptavka || !poptavka.id) throw new Error("neocekavana-odpoved");
    if (form.zakazka_id) {
      /* Nabídka je jen doplněk. Kdyby její selhání shodilo celé zakládání,
         přišel by makléř i o poptávku, kterou už server uložil. */
      try {
        await nabidniNemovitost(poptavka.id, form.zakazka_id, lang);
      } catch {
        toast.error(t("poptavka.chybaNabidky"));
      }
    }
    return poptavka;
  };

  const submit = async (openAfter = true) => {
    if (!form.contact_id) { toast.error(t("obchody.selectClient")); return; }
    if (!form.title.trim()) {
      toast.error(jeProdej ? t("realSlozka.adresaChybi") : t("obchody.dealNamePh"));
      return;
    }
    setSaving(true);
    try {
      if (jeProdej) {
        const zakazka = await submitProdej();
        onCreated?.(zakazka, { druh: "zakazka" });
        return;
      }
      if (jeKoupe) {
        const poptavka = await submitKoupe();
        onCreated?.(poptavka, { druh: "poptavka" });
        return;
      }
      const payload = {
        contact_id: form.contact_id,
        title: form.title.trim(),
        product_type: form.product_type,
        provider: form.provider.trim() || undefined,
        value_bj: form.value_bj ? Number(form.value_bj) : undefined,
        value_kc: form.value_kc ? Number(form.value_kc) : undefined,
        notes: form.notes.trim() || undefined,
        // Fázi vybere backend PODLE NÁZVU boardu (podpis → fáze Podpis, ne Schválení)
        board_pick: boardStage,
        stage_index: initialStageForBoard(boardStage),
        // QA-B / Raynet — uložit pravděpodobnost přímo na deal pro pipeline weighting
        params: { probability: Number(form.probability) || 50 },
      };
      const { data } = await api.post("/api/deals", payload);
      if (!openAfter && onCreatedStay) onCreatedStay(data);
      else onCreated?.(data);
    } catch {
      toast.error(t("obchody.cantCreate"));
    } finally {
      setSaving(false);
    }
  };

  /* Esc, klik na zatemnění i křížek okna jdou přes jednu bránu: rozepsaný
     obchod se nesmí ztratit omylem. Čísla (probability, stage_index) mají
     default → za „rozepsáno" se neberou. „Zrušit" dole je vědomá akce. */
  const zavritBezpecne = zavriNaPozadi({
    hodnoty: [form.title, form.provider, form.value_bj, form.value_kc, form.notes],
    onClose,
    lang,
  });
  const zmenOtevreno = (v) => { if (!v) zavritBezpecne(); };

  return (
    <Okno
      otevreno={open}
      zmenOtevreno={zmenOtevreno}
      nazev={t("obchody.newDeal", 'Nový obchod')}
      sirka="md"
      testid="new-deal-modal"
      paticka={(
        <>
          <button onClick={onClose} className="mr-auto min-h-[44px] px-3 text-sm text-[var(--muted)] transition-colors hover:text-[var(--ink)] md:min-h-[36px]" data-testid="new-deal-close">{t("obchody.cancel")}</button>
          {/* „Uložit" (= zůstat na nástěnce) u nemovitosti NENÍ. Prodej i koupě
              se zakládají jinou routou a ta se vždycky vrací přes `onCreated`,
              takže by tlačítko slíbilo setrvání a stejně by odnavigovalo.
              Radši ho nenabídnout, než lhát. */}
          {onCreatedStay && !jeReality && (
            <button
              onClick={() => submit(false)}
              disabled={saving}
              data-testid="new-deal-submit-stay"
              className="min-h-[44px] rounded-[var(--radius-sm)] border border-[var(--line)] bg-[var(--surface)] px-4 text-sm font-semibold text-[var(--ink)] transition-colors hover:bg-[var(--accent-soft)] disabled:opacity-50 md:min-h-[36px]"
            >
              {t("auto.spolecne.ulozit", 'Uložit')}
            </button>
          )}
          <button
            onClick={() => submit(true)}
            disabled={saving}
            data-testid="new-deal-submit"
            className="min-h-[44px] rounded-[var(--radius-sm)] bg-[var(--accent)] px-5 text-sm font-semibold text-[var(--on-accent)] transition-opacity hover:opacity-90 disabled:opacity-50 md:min-h-[36px]"
          >
            {saving ? t("common.saving", "Ukládám…") : (t("auto.newDealModal.ulozitAOtevrit", 'Uložit a otevřít'))}
          </button>
        </>
      )}
    >
        <div className="mb-4">
          {/* Raynet vzor: velký název případu rovnou nahoře, bez rámečku */}
          <input
            autoFocus
            value={form.title}
            onChange={(e) => set("title", e.target.value)}
            placeholder={jeProdej
              ? t("realSlozka.adresaPlaceholder", 'Adresa nemovitosti — Kotlářská 12, Brno')
              : t("auto.newDealModal.zadejNazevObchodnihoPripadu", 'Zadej název obchodního případu')}
            data-testid="new-deal-title"
            aria-label={jeProdej ? t("realSlozka.adresa") : t("obchody.dealNamePh")}
            className="w-full min-w-0 border-b-2 border-[var(--line)] bg-transparent pb-1.5 font-display text-xl font-semibold text-[var(--ink)] outline-none transition-colors focus:border-[var(--accent)]"
          />
        </div>

        <div className="space-y-3">
          {/* QA-B5: Čeho se to týká — klient (autocomplete místo selectu 98 položek) */}
          <div className="rounded-[var(--radius-sm)] border border-dashed border-[var(--accent)] bg-[var(--accent-soft)] p-2.5">
            <div className="mb-1.5 text-xs font-semibold text-[var(--accent-ink)]">{t("obchody.whatAbout")}</div>
            {contactId ? (
              <div className="rounded-[var(--radius-sm)] border border-[var(--line)] bg-[var(--bg)] px-3 py-2 text-sm text-[var(--ink)]" data-testid="new-deal-locked-client">
                <span className="text-[var(--muted)]">Klient: </span><b>{contactName || t("obchody.clientFromCard")}</b>
              </div>
            ) : (
              <ClientAutocomplete
                value={form.contact_id}
                onChange={(id) => set("contact_id", id)}
                testid="new-deal-client"
                placeholder={t("obchody.clientSearchPh")}
                allowCreate
                onCreateNew={createNewClient}
                selectedObj={newClientObj}
              />
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label={t("obchody.productType")}>
              <select
                value={form.product_type}
                onChange={(e) => set("product_type", e.target.value)}
                data-testid="new-deal-product"
                className={POLE}
              >
                {(povolitReality ? [...PRODUCT_OPTIONS, ...REALITNI_OPTIONS] : PRODUCT_OPTIONS).map((pt) => (
                  <option key={pt} value={pt}>{t(`dealmeta.products.${pt}`, PRODUCT_META[pt]?.label || pt)}</option>
                ))}
              </select>
            </Field>
            {jeProdej ? (
              <Field label={t("realSlozka.typNemovitosti")}>
                <select
                  value={form.typ_nemovitosti}
                  onChange={(e) => set("typ_nemovitosti", e.target.value)}
                  data-testid="new-deal-typ-nemovitosti"
                  className={POLE}
                >
                  {TYPY_NEMOVITOSTI.map((tp) => (
                    <option key={tp} value={tp}>{t(`zakazky.typ${tp.charAt(0).toUpperCase()}${tp.slice(1)}`)}</option>
                  ))}
                </select>
              </Field>
            ) : jeKoupe ? (
              <Field label={t("realSlozka.kterouNemovitostVolitelne")}>
                <select
                  value={form.zakazka_id}
                  onChange={(e) => {
                    const id = e.target.value;
                    set("zakazka_id", id);
                    /* Název obchodu se předvyplní z adresy, dokud si ho makléř
                       nepřepsal sám. Přepsaný název se NEPŘEPISUJE zpátky. */
                    const z = (Array.isArray(zakazky) ? zakazky : []).find((x) => x.id === id);
                    if (z && !form.title.trim()) set("title", `${t("realSlozka.koupePrefix")} ${z.adresa || ""}`.trim());
                  }}
                  data-testid="new-deal-zakazka"
                  className={POLE}
                >
                  <option value="">
                    {zakazky === null ? t("realSlozka.nacitamNemovitosti")
                      : zakazky === false ? t("realSlozka.nemovitostiChyba")
                      : zakazky.length ? t("realSlozka.zatimNevybrano")
                      : t("realSlozka.zadnaNemovitost")}
                  </option>
                  {(Array.isArray(zakazky) ? zakazky : []).map((z) => (
                    <option key={z.id} value={z.id}>{z.adresa}</option>
                  ))}
                </select>
              </Field>
            ) : (
              <Field label={t("obchody.provider")}>
                <Input value={form.provider} onChange={(v) => set("provider", v)} placeholder="ČSOB, Allianz, Conseq…" testid="new-deal-provider" />
              </Field>
            )}
          </div>

          {/* Šipky fází, pravděpodobnost a body u nemovitosti nedávají smysl:
              nová zakázka vždy začíná prvním krokem (Nábor / Ozval se) a
              provize z bytu se v bodech neměří. Pole se proto nekreslí —
              ne aby se vyplněná hodnota po odeslání tiše ztratila. */}
          {!jeReality && (
          <Field label={t("obchody.phase")}>
            {/* Raynet vzor: fáze jako procesní šipky. Vybraná fáze = jemná plocha
                v barvě fáze (--faze-*) + tučný text, ne bílá na plné barvě
                (jantarová fáze Podpis by s bílým textem měla 2,3 : 1). */}
            <div className="flex items-stretch" data-testid="new-deal-stage">
              {BOARD_COLUMNS.map((c, i) => {
                const on = boardStage === c.id;
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setBoardStage(c.id)}
                    data-testid={`new-deal-stage-${c.id}`}
                    aria-pressed={on}
                    title={c.title}
                    className={`flex-1 min-h-[44px] text-xs transition-colors md:min-h-[36px] ${on ? "font-bold text-[var(--ink)]" : "font-medium text-[var(--muted)]"}`}
                    style={{
                      background: on ? `color-mix(in srgb, var(--faze-${c.id}) 22%, var(--surface))` : "var(--bg)",
                      boxShadow: on ? `inset 0 -2px 0 var(--faze-${c.id})` : "inset 0 0 0 1px var(--line)",
                      clipPath: i === 0
                        ? "polygon(0 0, calc(100% - 10px) 0, 100% 50%, calc(100% - 10px) 100%, 0 100%)"
                        : "polygon(0 0, calc(100% - 10px) 0, 100% 50%, calc(100% - 10px) 100%, 0 100%, 9px 50%)",
                      paddingLeft: i === 0 ? 8 : 14,
                      paddingRight: 14,
                      marginLeft: i === 0 ? 0 : -6,
                    }}
                  >
                    {[t("auto.newDealModal.krokAnalyza", 'Analýza'), t("auto.newDealModal.krokProces", 'Proces'), t("auto.newDealModal.krokPodpis", 'Podpis'), t("auto.newDealModal.krokCerpani", 'Čerpání')][i]}
                  </button>
                );
              })}
            </div>
          </Field>
          )}

          {!jeReality && (
          <Field label={`${t("auto.newDealModal.pravdepodobnostUzavreni", 'Pravděpodobnost uzavření')} — ${form.probability} %`}>
            <input
              type="range"
              min="0" max="100" step="10"
              value={form.probability}
              onChange={(e) => set("probability", e.target.value)}
              data-testid="new-deal-probability"
              className="h-11 w-full accent-[var(--accent)] md:h-9"
            />
          </Field>
          )}

          <div className={jeReality ? "" : "grid grid-cols-2 gap-3"}>
            {!jeReality && (
              <Field label={t("obchody.estBj")}>
                <Input type="number" value={form.value_bj} onChange={(v) => set("value_bj", v)} placeholder="0" testid="new-deal-bj" />
              </Field>
            )}
            {!jeKoupe && (
              <Field label={jeProdej
                ? `${t("realSlozka.cenaNabidkova")} (${currency.symbol})`
                : `${t("obchody.valueCur")} (${currency.symbol})`}>
                <Input type="number" value={form.value_kc} onChange={(v) => set("value_kc", v)} placeholder="0" testid="new-deal-kc" />
              </Field>
            )}
          </div>

          <Field label={t("obchody.note")}>
            <textarea
              rows={3}
              value={form.notes}
              onChange={(e) => set("notes", e.target.value)}
              className={`${POLE} h-auto resize-none py-2`}
              data-testid="new-deal-notes"
            />
          </Field>
        </div>
    </Okno>
  );
}

const POLE = "h-11 w-full rounded-[var(--radius-sm)] border border-[var(--line)] bg-[var(--bg)] px-2.5 text-base text-[var(--ink)] outline-none transition-colors focus:border-[var(--accent)] md:h-9 md:text-sm";

function Field({ label, children }) {
  return (
    <div>
      <div className="mb-1 text-xs text-[var(--muted)]">{label}</div>
      {children}
    </div>
  );
}
function Input({ value, onChange, type = "text", placeholder, testid }) {
  return (
    <input
      type={type}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      data-testid={testid}
      inputMode={type === "number" ? "decimal" : undefined}
      className={POLE}
    />
  );
}
